import { GROUP_DISTANCE_THRESHOLD } from '../components/data-point/constants.js';
import { GroupDataPoint, DataPoint } from '../types/data-point.js';

/**
 * Given an unordered list sorts descendingly and groups the list values based
 * on the distance between them, considering the given threshold and an accessor
 * to the numeric value
 *
 * @param list - unordered list to group
 * @param threshold - maximum distance allowed between two numeric values
 * @param accessor - accessor to the numeric value to group by
 */
export const groupByThreshold = <T>(
  list: T[],
  threshold: number,
  accessor: (value: T) => number,
): T[][] => {
  // clone the array to avoid mutating the array passed as a parameter when sorting
  const cloned = [...list];
  cloned.sort((a, b) => accessor(b) - accessor(a));

  const groups: T[][] = [[]];
  let previousValue: number | null = null;
  for (const entry of cloned) {
    const value = accessor(entry);
    if (previousValue !== null && Math.abs(previousValue - value) > threshold) {
      groups.push([]);
    }
    const group = groups.at(-1);
    if (group !== undefined) {
      group.push(entry);
    }
    previousValue = value;
  }

  return groups.filter((group) => group.length > 0);
};

export const getGroupedDataPointsByCoordinate = (
  xPoints: DataPoint[],
  coordinate: 'y' | 'x',
): GroupDataPoint => {
  const pointsGrouped = groupByThreshold(
    xPoints,
    GROUP_DISTANCE_THRESHOLD,
    (closestPoint) => closestPoint[coordinate],
  );

  return pointsGrouped.filter((groupedPoint) => groupedPoint.length > 0);
};
